import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  DrawerBody, DrawerFooter, Typography, Button, Badge
} from '@one-thd/sui-atomic-components';
import { extend, QueryProvider } from '@thd-nucleus/data-sources';
import { AddToContext } from '../../AddToContext';
import { SCREENS, TRACK } from '../../AddToConstants';
import { AIListBuilder } from '../../common/ai-list-builder/AIListBuilder';
import { StarterListCard } from '../../common/tile-components/StarterListCard';
import { bathroomRenoMaterials } from './starter-lists-data/BathroomRenoMaterials';
import { kitchenRenoMaterials } from './starter-lists-data/KitchenRenoMaterials';
import { deckInstallationMaterials } from './starter-lists-data/DeckInstallationMaterials';
import { formatMaterials, generateId, triggerAnalytics } from '../../AddToHelpers';

export const StarterLists = ({ aiListBuilderData }) => {
  const {
    handleClose,
    setGroupedIntentsPreview,
    setScreen
  } = useContext(AddToContext);
  const {
    title = '', materials = [], refType = '', refId = ''
  } = aiListBuilderData || {};

  // 10.2 Starter Lists Overlay - Track when overlay is shown
  useEffect(() => {
    triggerAnalytics(TRACK.LIST_BUILDER_OVERLAY, {
      overlayType: 'starter lists'
    });
  }, []);

  const bathroomImageUrl = 'https://dam.thdstatic.com/content/production/JCx275_3poFpVKybhilh4g/'
    + 'wGgYvSErvMwsu6KspYITPg/optimizedFile/2023_ProCustom_Remodeler_Bathtub_Measure_0035_v2.jpg';

  const kitchenImageUrl = 'https://images.ctfassets.net/7rldri896b2a/3CgwZOQy5vLDqXBkQQMrpf/'
    + 'b5dee39a1ddf7156013c697c28edfac4/Main_Image.jpg';

  const deckImageUrl = 'https://dam.thdstatic.com/content/production/AAP6zd-8KWfDzqPYTAANZg/'
    + '38Zg3KeIJYjHjdSAEIuHnw/optimizedFile/71_2022_ePro_PowerTools_204995133_L_7621.jpg';

  // Map starter list titles to their material data
  const starterListMaterials = {
    'Bathroom Renovation': bathroomRenoMaterials,
    'Kitchen Renovation': kitchenRenoMaterials,
    'Deck Installation': deckInstallationMaterials
  };

  const handleStarterListClick = (listTitle) => {
    const rawMaterials = starterListMaterials[listTitle];

    if (rawMaterials) {
      // 10.2.1 Starter List Selection - Track specific starter list selection
      const selection = `starter list: ${listTitle}`;

      triggerAnalytics(TRACK.LIST_BUILDER_STARTER_LIST_SELECTION, {
        selection
      });

      // Format materials data to match AIListBuilder structure
      const generatedMaterials = formatMaterials(rawMaterials);

      // Set the materials and navigate to preview screen
      setGroupedIntentsPreview({
        sessionId: generateId(),
        groupedIntents: generatedMaterials,
        sourceType: 'starter_list',
        starterListName: listTitle
      });
      setScreen(SCREENS.GENERATED_LIST_INTENTS_PREVIEW);
    }
  };

  return (
    <>
      <DrawerBody overflow="scroll">
        <div className="sui-mb-6">
          <Typography variant="h3" weight="display" align="center">
            Pick a starter list to personalize
          </Typography>
          <div className="sui-flex sui-justify-center sui-mt-2">
            <Badge color="info" variant="regular" size="small">
              Beta
            </Badge>
          </div>
        </div>

        {/* Starter List Cards */}
        <div className="sui-grid sui-grid-cols-3 sui-gap-4 sui-mb-6">
          <StarterListCard
            title="Bathroom Renovation"
            image={bathroomImageUrl}
            onClick={() => handleStarterListClick('Bathroom Renovation')}
          />
          <StarterListCard
            title="Kitchen Renovation"
            image={kitchenImageUrl}
            onClick={() => handleStarterListClick('Kitchen Renovation')}
          />
          <StarterListCard
            title="Deck Installation"
            image={deckImageUrl}
            onClick={() => handleStarterListClick('Deck Installation')}
          />
        </div>

        {/* AI List Builder Section */}
        <div className="sui-mb-4">
          <Typography variant="h3" weight="display" align="center">
            Not seeing the project you need?
          </Typography>
        </div>

        <div className="sui-mt-4">
          <QueryProvider cacheKey='aiListBuilder'>
            <AIListBuilder
              name={title}
              materials={materials}
              hideTitle
              hideButtons
              refType={refType}
              refId={refId}
            />
          </QueryProvider>
        </div>
      </DrawerBody>
      <DrawerFooter
        position="center"
        style={{ boxShadow: '0 1px 8px 0 rgb(0 0 0 / 0.375)' }}
      >
        <Button
          data-testid="add-to-cancel-button"
          variant="secondary"
          fullWidth
          onClick={(__evt) => { handleClose(__evt, { type: 'close' }); }}
          type="submit"
        >
          Cancel
        </Button>
      </DrawerFooter>
    </>
  );
};

StarterLists.propTypes = {
  aiListBuilderData: PropTypes.shape({
    onNewIntentsSave: PropTypes.func,
    title: PropTypes.string,
    refType: PropTypes.string,
    refId: PropTypes.string,
    materials: PropTypes.arrayOf(PropTypes.shape({
      groupName: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.shape({
        quantity: PropTypes.number,
        product: PropTypes.string,
      }))
    }))
  })
};

StarterLists.defaultProps = {
  aiListBuilderData: null,
};

StarterLists.displayName = 'StarterLists';

